import { useCallback, useEffect, useState } from 'react';
import {
  dealConfigSchema,
  dealPickersSchema,
  formatMoney,
  type DealConfig,
  type DealEventKind,
  type DealLineItemStatus,
  type DealLineItemType,
  type DealRateUnit,
  type DealRevenueMode,
  type DealSource,
  type DealStatusCategory,
  type ForecastCategory,
  type Seniority,
} from '@forecastify/shared';
import type { useT } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { DEAL_TOPICS } from '../../live/topics';
import { useSession } from '../../session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · what every deal screen shares.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The money format, the vocabulary labels (one `t()` per branch with a literal key, so the keys
 * stay greppable), the refusal sentence, and the picker feeds the dialogs need.
 */

type T = ReturnType<typeof useT>;

const SYMBOLS: Readonly<Record<string, string>> = { HUF: 'Ft', EUR: '€', USD: '$', GBP: '£' };

/** `24 000 000 Ft` — the product's money format, from a wire string or a number. */
export function dealMoney(value: string | number, currencyCode: string): string {
  const amount = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(amount)) return '–';
  return formatMoney(amount, { code: currencyCode, symbol: SYMBOLS[currencyCode] ?? currencyCode });
}

/** `24 M Ft` / `850 e Ft` — the compact form for tiles and column heads. */
export function dealMoneyShort(value: string | number, currencyCode: string): string {
  const amount = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(amount) || amount === 0) return dealMoney(0, currencyCode);
  const symbol = SYMBOLS[currencyCode] ?? currencyCode;
  const abs = Math.abs(amount);
  const scaled =
    abs >= 1e6
      ? `${String(Math.round(amount / 1e5) / 10).replace('.', ',')} M`
      : `${String(Math.round(amount / 1e3))} e`;
  return ['EUR', 'USD', 'GBP'].includes(currencyCode)
    ? `${symbol} ${scaled}`
    : `${scaled} ${symbol}`;
}

export function categoryLabel(t: T, category: DealStatusCategory): string {
  if (category === 'won') return t('deal.category.won');
  if (category === 'lost') return t('deal.category.lost');
  return t('deal.category.open');
}

export function forecastLabel(t: T, category: ForecastCategory): string {
  if (category === 'commit') return t('deal.forecast.commit');
  if (category === 'best_case') return t('deal.forecast.best_case');
  if (category === 'omitted') return t('deal.forecast.omitted');
  return t('deal.forecast.pipeline');
}

/**
 * The forecast categories' dots — semantic, read-only (`0227`), so status tokens rather than
 * stored colours. Omitted is the disabled-glyph tone: it is the category that does not count.
 */
export const FORECAST_COLORS: Readonly<Record<ForecastCategory, string>> = {
  commit: 'var(--status-ok)',
  best_case: 'var(--status-info)',
  pipeline: 'var(--status-idle)',
  omitted: 'var(--text-3)',
};

export function sourceLabel(t: T, source: DealSource): string {
  if (source === 'referral') return t('deal.source.referral');
  if (source === 'outbound') return t('deal.source.outbound');
  if (source === 'existing') return t('deal.source.existing');
  if (source === 'tender') return t('deal.source.tender');
  if (source === 'partner') return t('deal.source.partner');
  return t('deal.source.inbound');
}

export function seniorityLabel(t: T, seniority: Seniority): string {
  if (seniority === 'junior') return t('deal.seniority.junior');
  if (seniority === 'senior') return t('deal.seniority.senior');
  return t('deal.seniority.medior');
}

export function revenueModeLabel(t: T, mode: DealRevenueMode): string {
  if (mode === 'recurring') return t('deal.revenueMode.recurring');
  if (mode === 'milestones') return t('deal.revenueMode.milestones');
  if (mode === 'manual') return t('deal.revenueMode.manual');
  return t('deal.revenueMode.one_off');
}

export function lineTypeLabel(t: T, type: DealLineItemType): string {
  if (type === 'tm') return t('deal.lineType.tm');
  if (type === 'license') return t('deal.lineType.license');
  if (type === 'option') return t('deal.lineType.option');
  return t('deal.lineType.fixed');
}

export function lineStatusLabel(t: T, status: DealLineItemStatus): string {
  if (status === 'open') return t('deal.lineStatus.open');
  if (status === 'closed') return t('deal.lineStatus.closed');
  return t('deal.lineStatus.draft');
}

export function rateUnitLabel(t: T, unit: DealRateUnit): string {
  if (unit === 'day') return t('deal.rateUnit.day');
  if (unit === 'month') return t('deal.rateUnit.month');
  return t('deal.rateUnit.hour');
}

export function eventKindLabel(t: T, kind: DealEventKind): string {
  if (kind === 'task') return t('deal.eventKind.task');
  if (kind === 'meeting') return t('deal.eventKind.meeting');
  if (kind === 'call') return t('deal.eventKind.call');
  if (kind === 'email') return t('deal.eventKind.email');
  if (kind === 'system') return t('deal.eventKind.system');
  return t('deal.eventKind.note');
}

/** Initials for an avatar — `Máriás Zsigmond` → `MZ`. */
export function initialsOf(name: string): string {
  return name
    .split(/\s+/u)
    .filter((part) => part !== '')
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * The sentence for a refused write. ⚠️ Branches on `code` and on the `details` the server put
 * there (rule 5) — a conflict's `reason`, a field error's `field` — never on the message.
 */
export async function dealRefusal(response: Response, t: T): Promise<string> {
  const envelope = (await response.json().catch(() => null)) as {
    error?: { code?: string; details?: { field?: string; code?: string; message?: string }[] };
  } | null;
  const code = envelope?.error?.code ?? 'unavailable';
  const details = envelope?.error?.details ?? [];
  const reason = details.find((detail) => detail.field === 'reason')?.code;
  const field = details.find((detail) => detail.field !== 'reason' && detail.field !== 'version');

  if (code === 'conflict') {
    if (reason === 'closed') return t('deal.error.refused.closed');
    if (reason === 'not_open_status') return t('deal.error.refused.not_open_status');
    if (reason === 'not_closed') return t('deal.error.refused.not_closed');
    return t('error.conflict');
  }
  if (code === 'validation_failed' && field?.field !== undefined && field.field !== '') {
    return t('deal.error.field', { field: field.field });
  }
  if (code === 'not_found') return t('error.not_found');
  if (code === 'validation_failed') return t('error.validation_failed');
  return t('error.unavailable');
}

/** A person a deal can name — owner, follower, next step, a placeholder's tentative person. */
export interface DealPerson {
  readonly id: string;
  readonly displayName: string;
  readonly avatarSeed: string | null;
}

/** A currency a deal's value can be in. */
export interface DealCurrency {
  readonly code: string;
  readonly name: string;
  readonly symbol: string;
}

/** A company a deal can be for. */
export interface DealClient {
  readonly id: string;
  readonly name: string;
  readonly color: string | null;
}

export interface DealFeeds {
  /** The vocabulary and settings; null until the first answer. */
  readonly config: DealConfig | null;
  readonly members: readonly DealPerson[];
  readonly clients: readonly DealClient[];
  readonly currencies: readonly DealCurrency[];
  readonly reload: () => Promise<void>;
}

/**
 * The pickers' data: the deal vocabulary, the people and the live clients. ⚠️ People and clients
 * come from `GET /deals/pickers`, which needs only `deal.view` — a role that manages deals without
 * `client.view` or `people.view` must still name a company and an owner. ⚠️ A failed feed is not
 * fatal — a dialog with an empty picker is a smaller loss than a screen that will not render.
 */
export function useDealFeeds(): DealFeeds {
  const session = useSession();
  const [config, setConfig] = useState<DealConfig | null>(null);
  const [members, setMembers] = useState<readonly DealPerson[]>([]);
  const [clients, setClients] = useState<readonly DealClient[]>([]);
  const [currencies, setCurrencies] = useState<readonly DealCurrency[]>([]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) return;
    const [configResponse, pickerResponse] = await Promise.all([
      session.request('/deal-settings').catch(() => null),
      session.request('/deals/pickers').catch(() => null),
    ]);
    if (configResponse?.ok === true) {
      const parsed = dealConfigSchema.safeParse(await configResponse.json());
      if (parsed.success) setConfig(parsed.data);
    }
    if (pickerResponse?.ok === true) {
      const parsed = dealPickersSchema.safeParse(await pickerResponse.json());
      if (parsed.success) {
        setMembers(
          parsed.data.members.map((m) => ({
            id: m.id,
            displayName: m.name,
            avatarSeed: m.avatarSeed,
          })),
        );
        setClients(parsed.data.clients);
        setCurrencies(parsed.data.currencies);
      }
    }
  }, [session]);

  useEffect(() => {
    void reload();
  }, [reload]);
  useLiveInvalidation(DEAL_TOPICS, reload);

  return { config, members, clients, currencies, reload };
}
